<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import ContractRestructuringContractList from '../../components/js/Components/ContractRestructuring/ContractRestructuringContractList.vue'
import RepurchasedContractList from '../../components/js/Components/ContractRestructuring/RepurchasedContractList.vue'
import AffectedElementList from '../../components/js/Components/ContractRestructuring/View/AffectedElementList.vue'
import ContractRestructuringActionSummary from '../../components/js/Components/ContractRestructuring/View/ContractRestructuringActionSummary.vue'
import ButtonAll from '../../components/js/Components/UI/Button/ButtonAll.vue'
import ButtonLink from '../../components/js/Components/UI/Button/ButtonLink.vue'
import BlockContent from '../../components/js/Components/UI/Content/BlockContent.vue'
import FieldCreator from '../../components/js/Components/UI/Form/FieldCreator.vue'
import FormCreator from '../../components/js/Components/UI/Form/FormCreator.vue'
import LazyContent from '../../components/js/Components/UI/Loading/LazyContent.vue'
import PlaceholderContent from '../../components/js/Components/UI/Loading/PlaceholderContent.vue'
import ModalConfirmation from '../../components/js/Components/UI/Modal/ModalConfirmation.vue'
import { useRestructuringApi } from '../../Composables/Restructuring/useRestructuringApi.ts'
import useLoading from '../../Composables/useLoading.ts'
import useModal from '../../Composables/useModal.ts'
import { ContractRestructuringActionType } from '../../Constants/ContractRestructuringActionType.ts'
import ContractRestructuringStatus from '../../Constants/ContractRestructuringStatus.ts'
import { Routes } from '../../Constants/Routes.ts'
import type { Contract } from '../../Types/contract'
import type { DatatableCallback } from '../../Types/datatable'
import type { FormCreatorType } from '../../Types/form'
import type {
  AnyRepurchasedContract,
  ContractRestructuring,
  OutstandingRepurchaseAction,
} from '../../Types/restructuring'

const props = defineProps<{
  restructuring: ContractRestructuring | null
}>()

const emit = defineEmits<{
  cancel: []
  update: [{ restructuring: ContractRestructuring }]
}>()

const { d, t } = useI18n()

const { loading } = useLoading(() => !props.restructuring)
const { loading: editMode, setLoading: setEditMode } = useLoading(false)
const { loading: canceling } = useLoading(false)
const { loading: submitting, setLoading: setSubmitting } = useLoading(false)

const restructuringId = computed<number | null>(() => props.restructuring?.id ?? null)
const { cancel, getContracts } = useRestructuringApi(restructuringId)

const {
  modal: cancelConfirmationModal,
  open: openConfirmationModal,
  close: closeConfirmationModal,
} = useModal({
  prefix: 'restructuring-cancelation',
})

const form = ref<FormCreatorType>()

const restructuringTarget = computed<RouteLocationRaw>(() => ({
  name: Routes.RESTRUCTURING_EDIT,
  params: {
    id: restructuringId.value,
  },
}))

const readonly = computed(() =>
  [ContractRestructuringStatus.DONE, ContractRestructuringStatus.CANCELED].includes(props.restructuring?.status),
)

async function fetchContracts(callback: DatatableCallback<Contract>): Promise<void> {
  const contracts = await getContracts()

  callback({ data: contracts })
}

function submit() {
  form.value?.methods()
}

function afterSubmit(restructuring: ContractRestructuring) {
  setEditMode(false)
  emit('update', { restructuring })
  form.value?.get()
}

async function cancelRestructuring() {
  closeConfirmationModal()
  canceling.value = true
  await cancel()
  canceling.value = false

  emit('cancel')
}

function updateContracts(contracts: AnyRepurchasedContract[]): void {
  if (!props.restructuring?.outstandingRepurchase) {
    return
  }

  function update(action: OutstandingRepurchaseAction): void {
    action.contracts = contracts
    action.repurchaseAmount = contracts.reduce((total, contract) => total + Number(contract.repurchaseAmount), 0)
  }

  update(props.restructuring.outstandingRepurchase)

  for (const action of props.restructuring.actions) {
    if (action.actionType !== ContractRestructuringActionType.OUTSTANDING_REPURCHASE) {
      continue
    }

    update(action as OutstandingRepurchaseAction)
  }
}
</script>

<template>
  <FormCreator
    v-if="restructuring"
    endpoint="contracts/restructuring"
    method="patch"
    :id="restructuring.id"
    ref="form"
    src-error="formrestructuration-information"
    class-to-add="d-none"
    @is-sending="setSubmitting"
    @form-return="afterSubmit"
  />

  <!-- Information -->
  <div class="row">
    <div class="col-md-6">
      <BlockContent inner-bloc-class="border-contract-yellow">
        <template #header-title>
          <div class="d-flex justify-content-between">
            <h5 class="text-primary">
              {{ t('restructuring.detail.information.title') }}
            </h5>

            <div v-if="restructuring">
              <ButtonAll
                v-if="!editMode"
                margin="no"
                type="add"
                icon="modify"
                size="small"
                class="mr-2"
                label="button.update"
                width="aligned"
                @click="setEditMode(true)"
              />
            </div>
          </div>
        </template>

        <LazyContent :loading="loading">
          <div class="row mt-1">
            <div class="col-md-4">
              <b>{{ t('restructuring.fields.label.label') }}</b>
            </div>
            <div class="col-md-8">
              <template v-if="!editMode">
                {{ restructuring?.label ?? '-' }}
              </template>
              <template v-else>
                <FieldCreator
                  class="mt-n2 mb-4"
                  name="label"
                  form-ref="formrestructuration-information"
                  domain="restructuring"
                  :form-data="form as Object"
                />
              </template>
            </div>
          </div>
          <div class="row mt-1">
            <div class="col-md-4">
              <b>{{ t('restructuring.fields.createdAt.label') }}</b>
            </div>
            <div class="col-md-8">
              {{ restructuring?.createdAt ? d(restructuring.createdAt, 'date') : '-' }}
            </div>
          </div>
          <div class="row mt-1">
            <div class="col-md-4">
              <b>{{ t('restructuring.fields.createdBy.label') }}</b>
            </div>
            <div class="col-md-8">
              {{ restructuring?.createdBy.name ?? '-' }}
            </div>
          </div>
          <div v-if="editMode" class="d-flex justify-content-between mt-4">
            <ButtonAll
              margin="no"
              label="button.cancel"
              type="outline_info"
              :loading="submitting"
              @click="setEditMode(false)"
            />
            <ButtonAll margin="no" label="button.update" type="validation" :loading="submitting" @click="submit" />
          </div>
          <div v-else class="d-flex justify-content-end mt-4">
            <ButtonAll
              v-if="restructuring?.cancelable"
              margin="no"
              label="restructuring.actions.cancel"
              type="warning"
              :loading="canceling"
              @click="openConfirmationModal"
            />
            <div
              v-else-if="restructuring?.status !== ContractRestructuringStatus.CANCELED"
              class="bg-pale-warning b-1 border-light p-20 text-center"
            >
              <strong>{{ t('restructuring.error.not_cancelable') }}</strong>
            </div>
          </div>

          <template #loader>
            <PlaceholderContent :rows="4" />
          </template>
        </LazyContent>
      </BlockContent>
    </div>
    <div class="col-md-6">
      <BlockContent inner-bloc-class="border-contract-yellow">
        <template #header-title>
          <div class="d-flex justify-content-between">
            <h5 class="text-primary">
              {{ t('restructuring.detail.actions.title') }}
            </h5>
            <ButtonLink
              v-if="restructuring?.status === ContractRestructuringStatus.PENDING"
              label="restructuring.actions.continue"
              type="validation"
              :href="restructuringTarget"
            />
          </div>
        </template>

        <LazyContent :loading="loading">
          <template v-for="action in restructuring?.actions" :key="action.id">
            <ContractRestructuringActionSummary :action="action" />
          </template>

          <template #loader>
            <PlaceholderContent :rows="4" />
          </template>
        </LazyContent>
      </BlockContent>
    </div>
  </div>

  <!-- Restructured contracts -->
  <div class="row mt-2">
    <div class="col-md-12">
      <BlockContent inner-bloc-class="border-contract-yellow">
        <template #header-title>
          <div class="d-flex justify-content-between">
            <h5 class="text-primary">
              {{ t('restructuring.detail.contracts.title') }}
            </h5>
          </div>
        </template>

        <div class="mt-1">
          <LazyContent :loading="loading">
            <RepurchasedContractList
              v-if="restructuring?.outstandingRepurchase"
              :action="restructuring?.outstandingRepurchase"
              :readonly="readonly"
              @update:contracts="updateContracts"
            />
            <ContractRestructuringContractList v-else @fetch="fetchContracts" />

            <template #loader>
              <PlaceholderContent :rows="4" />
            </template>
          </LazyContent>
        </div>
      </BlockContent>
    </div>
  </div>

  <!-- Affected elements -->
  <div class="row mt-2">
    <div class="col-md-12">
      <BlockContent inner-bloc-class="border-contract-yellow">
        <template #header-title>
          <div class="d-flex justify-content-between">
            <h5 class="text-primary">
              {{ t('restructuring.detail.affected_elements.title') }}
            </h5>
          </div>
        </template>

        <div class="mt-1">
          <LazyContent :loading="loading">
            <AffectedElementList v-if="restructuring" :restructuring="restructuring" />

            <template #loader>
              <PlaceholderContent :rows="4" />
            </template>
          </LazyContent>
        </div>
      </BlockContent>
    </div>
  </div>

  <ModalConfirmation
    ref="cancelConfirmationModal"
    title="restructuring.actions.confirmCancelation"
    button-confirm-title="main.bool.true"
    button-cancel-title="main.bool.false"
    @confirm="cancelRestructuring"
  />
</template>
